import React, { useEffect, useRef, useState } from "react"
import {
  ContentfulRichTextGatsbyReference,
  renderRichText,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import { useContentfulEntry } from "../../hooks/useContentfulEntry"
import { useContentfulImage } from "../../hooks/useContentfulImage"
import { useContentfulPDF } from "../../hooks/useContentfulPDF"
import { useContentfulInlines } from "../../hooks/useContentfulInline"
import { Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import HubspotForm from "react-hubspot-form"
import QuotesComponent from "../../components/QuotesComponent/QuotesComponent"
import loadable from "@loadable/component"

type RenderRichContentProps = {
  content: RenderRichTextData<ContentfulRichTextGatsbyReference>
  role: any
  classNames: any
}

// const LazyHubspotForm = loadable(() => import("react-hubspot-form"), {
//   ssr: false,
//   fallback: <div>Loading form...</div>,
// })

export const RenderRichContent = ({
  content,
  role,
  classNames,
}: RenderRichContentProps) => {
  const formRef = useRef(null)
  const [showForm, setShowForm] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowForm(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

    if (formRef.current) {
      observer.observe(formRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Add custom options to `renderRichText` function if necessary
  const options = {
    renderText: text => {
      return text.split("\n").reduce((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment]
      }, [])
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (node, children) => {
        return <h1 className={classNames}>{children}</h1>
      },
      [BLOCKS.HEADING_2]: (node, children) => {
        return <h2 className={classNames}>{children}</h2>
      },
      [BLOCKS.HEADING_3]: (node, children) => {
        return <h3 className={classNames}>{children}</h3>
      },
      [BLOCKS.HEADING_4]: (node, children) => {
        return <h4 className={classNames}>{children}</h4>
      },
      [BLOCKS.HEADING_5]: (node, children) => {
        return <h5 className={classNames}>{children}</h5>
      },
      [BLOCKS.HEADING_6]: (node, children) => {
        return <h6 className={classNames}>{children}</h6>
      },
      [BLOCKS.PARAGRAPH]: (node, children) => {
        if (children.length <= 2 && role === "legal-link") {
          return ""
        }

        return <p className={classNames}>{children}</p>
      },
      [BLOCKS.UL_LIST]: (node, children) => {
        if (role === "legal-link") {
          return (
            <li className={"lg:flex lg:justify-between lg:flex-row text-white"}>
              {children}
            </li>
          )
        } else if (role === "disc-list") {
          return <ul className={"list-disc"}>{children}</ul>
        } else {
          return <ul className={"ml-0"}>{children}</ul>
        }
      },
      [BLOCKS.LIST_ITEM]: (node, children) => {
        if (role === "legal-link") {
          return (
            <div
              className={
                "mr-10 text-white text-opacity-70 hover:text-opacity-100"
              }
            >
              {children}
            </div>
          )
        } else if (role === "disc-list") {
          return <li className="list-disc">{children}</li>
        } else {
          return <li>{children}</li>
        }
      },
      [INLINES.ENTRY_HYPERLINK]: node => {
        //TO DO: Add Legal links as Internal Entry Hyperlinks
        try {
          const entry = useContentfulEntry(node.data.target.sys.id)

          if (role === "headerlink") {
            return (
              <Link
                to={`/${entry}`}
                className={
                  "hover:font-semibold hidden lg:block xl:block relative py-9 font-medium text-black"
                }
                activeClassName={"font-semibold"}
              >
                {node.content[0].value}
              </Link>
            )
          } else if (role === "side-drawer-link") {
            return (
              <Link
                to={`/${entry}`}
                className={"hover:font-semibold block xl:hidden relative"}
                activeClassName={"font-semibold"}
              >
                {node.content[0].value}
              </Link>
            )
          } else if (role === "footerlink") {
            return (
              <Link
                to={`/${entry}`}
                target={"_self"}
                className={"text-white text-opacity-70 hover:text-opacity-100"}
              >
                {node.content[0].value}
              </Link>
            )
          } else if (role === "dropdownlink") {
            return (
              <Link
                to={`/${entry}`}
                className={"items-center p-4 hover:bg-gray-300 relative block"}
                activeClassName={"font-semibold"}
              >
                {node.content[0].value}
              </Link>
            )
          } else if (role === "dropdownlinkWithIcon") {
            return (
              <Link
                to={`/${entry}`}
                className={"ml-4 mr-8"}
                activeClassName={"font-semibold"}
              >
                {node.content[0].value}
              </Link>
            )
          } else if (role === "legal-link") {
            return (
              <Link
                to={`/${entry}`}
                className={"text-white text-opacity-70 hover:text-opacity-100"}
              >
                {node.content[0].value}
              </Link>
            )
          } else if (role === "headerlink") {
            return (
              <Link
                to={`/${entry}`}
                className={
                  "hover:font-semibold hidden lg:block xl:block relative"
                }
              >
                {node.content[0].value}
              </Link>
            )
          } else if (role === "sidedrawer-dropdown") {
            return (
              <Link
                to={`/${entry}`}
                className={
                  "hover:font-semibold text-edc-dark-grey block my-1 xl:hidden relative"
                }
              >
                {node.content[0].value}
              </Link>
            )
          } else if (role === "talksales") {
            return (
              <Link
                to={`/${entry}`}
                className={"text-edc-light-blue hover:underline"}
                target={"_blank"}
              >
                {node.content[0].value}
              </Link>
            )
          }
          return (
            <span style={{ display: "inline-block" }}>
              <Link
                to={`/${entry}`}
                className={"text-[#006ef5] hover:underline"}
              >
                {node.content[0].value}
              </Link>
            </span>
          )
        } catch (err) {
          return (
            <span style={{ display: "inline-block" }}>
              <a>{node.content[0].value}</a>
            </span>
          )
        }
      },
      [INLINES.HYPERLINK]: node => {
        const { uri } = node.data
        const isInternalLink = uri.startsWith("#")

        if (
          node.data.uri.indexOf("youtube-nocookie.com/embed") !== -1 ||
          (node.data.uri.indexOf("youtube.com/embed") !== -1 &&
            role === "landingpage-video")
        ) {
          return (
            <iframe
              src={node.data.uri}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture ; fullscreen ; clipboard-write;"
              allowFullScreen
              className={"md:min-h-[500px] min-h-[300px] max-w-full"}
            />
          )
        } else if (
          node.data.uri.indexOf("youtube-nocookie.com/embed") !== -1 ||
          node.data.uri.indexOf("youtube.com/embed") !== -1
        ) {
          return (
            <iframe
              src={node.data.uri}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture ; fullscreen"
              style={{ minHeight: 400 }}
            />
          )
        } else if (role === "footerlink") {
          return (
            <a
              href={node.data.uri}
              target={"_self"}
              className={"text-white text-opacity-70 hover:text-opacity-100"}
            >
              {node.content[0].value}
            </a>
          )
        } else if (role === "gftg-link") {
          return (
            <a
              href={node.data.uri}
              target={"_self"}
              className={
                "text-[#006ef5] text-opacity-70 hover:text-opacity-100 hover:underline"
              }
            >
              {node.content[0].value}
            </a>
          )
        } else if (role === "headerlink") {
          return (
            <a
              href={node.data.uri}
              className={
                "hover:font-semibold hidden lg:block xl:block relative"
              }
            >
              {node.content[0].value}
            </a>
          )
        } else if (role === "side-drawer-link") {
          return (
            <a
              href={node.data.uri}
              className={"hover:font-semibold block xl:hidden relative"}
            >
              {node.content[0].value}
            </a>
          )
        } else if (role === "blog-article") {
          return (
            <a
              href={node.data.uri}
              target={"_self"}
              className={"text-[#006ef5] hover:underline"}
            >
              {node.content[0].value}
            </a>
          )
        } else {
          return (
            <a
              href={node.data.uri}
              target={isInternalLink ? "_self" : "_blank"}
              className={"text-[#006ef5] hover:underline"}
            >
              {node.content[0].value}
            </a>
          )
        }
      },
      [INLINES.ASSET_HYPERLINK]: node => {
        const pdf = useContentfulPDF(node.data.target.sys.id)
        return (
          <a
            href={pdf.node.file.url}
            target="_blank"
            className={"text-[#006ef5] hover:underline"}
          >
            {node.content[0].value}
          </a>
        )
      },
      [BLOCKS.EMBEDDED_ASSET]: node => {
        const asset = useContentfulImage(node.data.target.sys.id)
        const pdf = useContentfulPDF(node.data.target.sys.id)

        if (pdf) {
          return <a href={pdf.node.file.url}>{pdf.node.file.fileName}</a>
        } else if (role === "dropdownlink") {
          return (
            <img
              src={asset.node.file.url}
              className={"inline"}
              alt={asset?.node.description || "image"}
            />
          )
        } else if (asset?.node?.file?.contentType.indexOf("image/svg+xml") > -1) {
          return (
            <img
              src={asset?.node.file.url}
              alt={asset?.node.description || "image"}
            />
          )
        } else if (asset?.node?.file?.contentType.indexOf("image/") > -1) {
          if (role === "dropdownlinkWithIcon") {
            return (
              <img
                src={asset.node.file.url}
                alt={"icon"}
                loading="eager"
                className={"object-contain w-[20px] h-[20px] mb-0"}
              />
            )
          }

          return (
            <>
              <GatsbyImage
                image={asset.node.gatsbyImageData}
                alt={asset.node.file.fileName}
                loading="eager"
                alt="icon"
              />
            </>
          )
        } else if (asset?.node?.file?.contentType.indexOf("video/") > -1) {
          return (
            <>
              <video
                src={asset.node.file.url}
                controls
                style={{ maxWidth: "100%" }}
              ></video>
            </>
          )
        } else {
          return null
        }
      },
      [INLINES.EMBEDDED_ENTRY]: node => {
        const inlineEntry = useContentfulInlines(node.data.target.sys.id)
        if (
          inlineEntry?.internal?.type ===
          "ContentfulContentBlockJumpLinkComponent"
        ) {
          return (
            <div id={inlineEntry.identifier} className={"-translate-y-24"} />
          )
        } else if (
          inlineEntry?.internal?.type === "ContentfulContentBlockHubSpotForm"
        ) {
          if (inlineEntry.meetingForm) {
            return (
              <div
                className="meetings-iframe-container"
                data-src={inlineEntry.formId}
              ></div>
            )
          }

          return (
            <div ref={formRef}>
              {/* {showForm && ( */}
              <HubspotForm
                portalId={inlineEntry.portalId}
                formId={inlineEntry.formId}
                region={inlineEntry.region}
                loading={<div>Loading...</div>}
              />
              {/* )} */}
            </div>
          )
        } else if (
          inlineEntry?.internal?.type ===
          "ContentfulEdc22ContentBlockImageAndTextLink"
        ) {
          return (
            <div className="lg:w-[1100px] h-full">
              <a href={inlineEntry.imageLink}>
                <GatsbyImage
                  image={inlineEntry.image.gatsbyImageData}
                  alt={inlineEntry.blockTitle}
                />
              </a>
            </div>
          )
        } else if (
          inlineEntry?.internal?.type === "ContentfulEdc22ContentBlockSlider"
        ) {
          return <QuotesComponent data={inlineEntry} isFullScreen={false} />
        } else if (inlineEntry[1]?.includes("trial")) {
          return (
            <div className={"w-full flex"}>
              <Link
                to={"/" + inlineEntry[3]}
                className={
                  "mx-auto bg-edc-green border-2 border-edc-green min-w-[138px] px-4 py-2 rounded-3xl font-medium text-center cursor-pointer text-white"
                }
              >
                {inlineEntry[1]}
              </Link>
            </div>
          )
        } else if (inlineEntry[1]?.includes("tickets")) {
          return (
            <div className={"w-full flex"}>
              <Link
                to={inlineEntry[0]}
                className={
                  "mx-auto bg-[#006ef5] min-w-[138px] px-4 py-2 rounded-3xl font-medium text-center cursor-pointer text-white block !my-4"
                }
              >
                {inlineEntry[1]}
              </Link>
            </div>
          )
        } else {
          return (
            <div className={"w-full flex"}>
              <Link
                to={inlineEntry[0]}
                target={"_blank"}
                className={
                  "mx-auto bg-[#006ef5] border-2 border-[#006ef5] min-w-[138px] px-4 py-2 rounded-3xl font-medium text-center cursor-pointer text-white"
                }
              >
                {inlineEntry[1]}
              </Link>
            </div>
          )
        }
      },
    },
  }
  return <>{content && renderRichText(content, options)}</>
}
